import { useState, type FormEvent, type ReactElement } from 'react';
import { X } from 'lucide-react';
import type { BoardMember, BoardRole } from '@molt/shared';
import {
  Avatar,
  Button,
  IconButton,
  Menu,
  ProfileLink,
  profileName,
  TextField,
} from '@/components';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import type { LocaleFormattingTranslate } from '@/lib/locale-formatting';

/**
 * `FE-10.2` — the board member list, add-member and remove-member surfaces.
 * Chunk `D4`, agent D. `D4-BRIEF.md` §6.5 is the contract.
 *
 * ---------------------------------------------------------------------------
 * PRESENTATIONAL. IT FETCHES NOTHING AND MUTATES NOTHING
 * ---------------------------------------------------------------------------
 * `BoardMenuPanel` owns the queries and the mutations, which is the house
 * pattern `WorkspaceMemberList` + `w.$workspaceId.members.tsx` already ships.
 * This file has no `announce()` call of its own for the same reason
 * `WorkspaceMemberList` has none — the composer owns every announcement.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THREE ABSENCES, EACH FOR A DIFFERENT REASON — DO NOT COLLAPSE THEM
 * ---------------------------------------------------------------------------
 *  - **No add form when `canManageMembers` is false.** `FR-PERM-018` AC4 says
 *    so in as many words: *"given a board member without invite rights … then
 *    no add-member control is offered"*. This is the one place in D4 where a
 *    requirement overrides "locked, never hidden", and it overrides it because
 *    it is explicit. The member **list** still renders.
 *  - **No remove and no role control on the viewer's own row.** Self-removal is
 *    *leave*, and leave is `FE-39.2`'s (`FR-PERM-031`). `WorkspaceMemberList`
 *    renders no role control on the viewer's own row for the same reason —
 *    this file additionally suppresses *remove* on that row, which
 *    `WorkspaceMemberList` never offers at all.
 *  - **The role control offers all three board roles.** P2-27 makes `observer`
 *    assignable from the same admin-only control as admin/normal, with the
 *    read-only capability stated in the menu item.
 *
 * ⚠ **`memberErrors` is keyed by user id.** `FR-PERM-032`'s last-admin refusal
 * is *"explained where it happens"* — on the row whose control was pressed,
 * never in a page-level `Notice` and never in a toast. A single shared error
 * string is how that refusal ends up under the wrong member.
 *
 * ⚠ **Rows are not stretched links.** Defect-ledger class 5 warns that
 * `toContainText` is unsafe on a stretched row; these rows are not
 * navigations at all and carry no anchor — there is nothing here for a click
 * on the row itself to do, and no `<a>` wraps it. A spec asserting against a
 * row should query the row's own text nodes and controls directly rather than
 * assuming the "stretched card" idiom other surfaces use.
 *
 * ⚠ **`publicProfileSchema.initials` has `.max()` and no `.min()`** — an empty
 * string is an accepted row. Render through `Avatar`, which already ships the
 * `displayName` → `@username` → `"Member"` fallback chain. Do not re-implement
 * it and do not "fix" the schema.
 *
 * ---------------------------------------------------------------------------
 * DIVERGENCE FROM `WorkspaceMemberList` — DELIBERATE, NOT ACCIDENTAL
 * ---------------------------------------------------------------------------
 *  - **A remove control exists here and has no equivalent there.** Workspace
 *    membership removal is `FE-39.2`'s too, but on the *board* side D4 owns
 *    the wire (`DELETE /boards/:id/members/:userId`) and the item text
 *    ("add-member, remove-member and the role control") names it explicitly.
 *  - **The row shows `@username` as its own element, not only inside the
 *    fallback chain.** `WorkspaceMemberList` renders a single `profileName()`
 *    string; `D4-BRIEF.md` §6.5's row anatomy lists display name and
 *    `@username` as two separate segments. Showing both verbatim would
 *    duplicate the username when `displayName` is `''` — `profileName()`
 *    already falls back to `@username` in that case — so the second segment
 *    renders only when it would add information: `displayName !== '' &&
 *    username !== ''`.
 *  - **The role trigger's accessible name follows §7's contract exactly**
 *    (`"Member — change <name>'s role"`), not `WorkspaceMemberList`'s
 *    `"Role of <name>: <label>"` — `D4-BRIEF.md` §7 fixes the pattern here in
 *    different words than `D2` used, and this file follows the brief that
 *    names it rather than the older prose.
 *  - **The role `Menu`'s wiring — `onValueChange` on the section, a no-op
 *    `onSelect` per item, and no `disabled` on the trigger itself (only
 *    `aria-busy`) — is copied from `WorkspaceMemberList` verbatim**, per the
 *    brief's instruction to follow its shipped wiring "rather than inventing
 *    a second one."
 */
export interface BoardMemberListProps {
  members: readonly BoardMember[];
  /** The viewer's own id, so their row renders no remove and no role control — §2.4. */
  viewerId: string | undefined;
  /** `requireBoardMemberManager`'s answer — §5.6. False → the add form is ABSENT (AC4). */
  canManageMembers: boolean;
  /** Board admin — the only role that may change roles. False → the control is absent per row. */
  canChangeRoles: boolean;
  onAddMember: (handle: { email: string } | { username: string }) => void;
  onRemoveMember: (userId: string) => void;
  onChangeRole: (userId: string, role: BoardRole) => void;
  addBusy?: boolean | undefined;
  busyMemberId?: string | null | undefined;
  /** The add form's own error slot — the server's message, verbatim. */
  addError?: string | undefined;
  /** ⚠ Keyed BY USER ID: the last-admin 409 renders on the row it happened to — §5.9. */
  memberErrors?: Readonly<Record<string, string>>;
  className?: string;
}

/**
 * `'admin' | 'normal' | 'observer'` → `Admin` | `Member` | `Observer`. Total.
 *
 * FE-80.1 — was an exported `Record<BoardRole, string>` module constant, which
 * cannot call `t`. Nothing outside this file imported it (grepped, `src` and
 * `e2e`), so it becomes a function of the translator rather than gaining a
 * second reader.
 *
 * ⚠ THREE LITERAL `t('…')` ARMS, NOT `t(ROLE_KEYS[role])`. The key-record form
 * is shorter and hides all three values from the `/\bt\(\s*'([^']+)'/g` anchor
 * every routing pin in this workstream uses — the board-table slice's residual
 * 6, where an `atLeast` guard sat one under the truth and passed a mutation
 * that deleted a real call site.
 *
 * ⚠ THE PARAMETER `role` IS AN API VALUE AND STAYS ONE. It is the same
 * `BoardRole` the radio section casts straight back into the PATCH body; only
 * the returned string is displayed, and only the returned string is routed.
 *
 * ⚠ `t` is REQUIRED and never defaulted, for the reason `lib/card-title.ts`
 * records: a defaulted translator lets one caller fall through to a default
 * that looks correct, and only a required one makes `tsc` enumerate the sites.
 */
function boardRoleLabel(t: LocaleFormattingTranslate, role: BoardRole): string {
  return {
    admin: t('boardMembers.role.option.admin'),
    normal: t('boardMembers.role.option.normal'),
    observer: t('boardMembers.role.option.observer'),
  }[role];
}

/**
 * P2-27: all three board roles are assignable. Observer stays in the same
 * control so an admin can both set and undo read-only access.
 */
const ASSIGNABLE_BOARD_ROLES: readonly BoardRole[] = ['admin', 'normal', 'observer'];

function boardRoleDescription(t: LocaleFormattingTranslate, role: BoardRole): string {
  return {
    admin: t('boardMembers.role.description.admin'),
    normal: t('boardMembers.role.description.normal'),
    observer: t('boardMembers.role.description.observer'),
  }[role];
}

export function BoardMemberList({
  members,
  viewerId,
  canManageMembers,
  canChangeRoles,
  onAddMember,
  onRemoveMember,
  onChangeRole,
  addBusy = false,
  busyMemberId = null,
  addError,
  memberErrors = {},
  className,
}: BoardMemberListProps): ReactElement {
  const { t } = useInterfaceLanguage();

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/*
        Explicit roles, not left to the implicit ones: Tailwind's preflight
        sets `list-style: none` on every `ul`, which strips the implicit
        `list`/`listitem` roles in WebKit/VoiceOver (jsdom applies no CSS and
        cannot catch this — `WorkspaceMemberList`'s own note, reused here).
        `jsx-a11y/no-redundant-roles` is not configured in this repo's
        `eslint.config.js`, so the explicit role is not a lint conflict.
      */}
      <ul role="list" className="flex flex-col gap-2">
        {members.map((member) => {
          const isViewer = viewerId !== undefined && member.id === viewerId;
          const busy = busyMemberId === member.id;
          const name = profileName(member);
          // See the file docstring: only shown when it would add information
          // beyond what `profileName()`'s own fallback chain already said.
          const showUsername = member.displayName !== '' && member.username !== '';
          const showRemove = canManageMembers && !isViewer;
          const showRoleControl = canChangeRoles && !isViewer;
          const rowError = memberErrors[member.id];

          return (
            <li
              key={member.id}
              role="listitem"
              className="flex flex-col gap-1 rounded-card border border-border-strong bg-surface-raised p-3"
            >
              {/*
                Not a stretched link — see the file docstring. No anchor wraps
                this row and no click on the row itself does anything; every
                affordance is its own control.
              */}
              <div className="flex items-center gap-3">
                <Avatar profile={member} size="sm" decorative />
                <span className="min-w-0 flex-1 truncate text-ink">
                  <ProfileLink profile={member} />
                  {showUsername ? (
                    <span className="ml-1 text-ink-muted">@{member.username}</span>
                  ) : null}
                </span>

                {/*
                  ⚠ OUTSIDE the truncating span, and `shrink-0` — found by the
                  `D4` screenshot pass at 1440×900.

                  This marker was a sibling of the name INSIDE that span, which
                  reads correctly and truncates away exactly when the name is
                  long enough to overflow a 320px panel row. It is the only
                  thing on the row explaining why this one row carries no
                  controls, so losing it silently is the presentation half of
                  the bug its own comment set out to prevent — the absent
                  controls reading as a rendering fault rather than as "this is
                  you" (`WorkspaceMemberList`'s reasoning, reused).

                  The name still truncates; the marker survives.
                */}
                {isViewer ? (
                  <span className="shrink-0 text-sm text-ink-muted">
                    {t('boardMembers.you')}
                  </span>
                ) : null}
                {/*
                  ⚠ TWO KEYS FOR ONE BADGE, and it is WCAG 2.5.3 rather than
                  duplication: `Guest` is the visible text, `Single-board
                  guest` is the accessible name that REPLACES it. They are
                  deliberately not prefix-related — the visible word is already
                  the head noun of the longer one, and `FR-PERM-023:655,659`
                  binds `Guest` with `exact: true`.
                */}
                {member.isGuest ? (
                  <span
                    aria-label={t('boardMembers.guestLabel')}
                    className="shrink-0 rounded-card border border-warn bg-surface px-1.5 py-0.5 text-xs font-medium text-warn"
                  >
                    {t('boardMembers.guest')}
                  </span>
                ) : null}

                {showRoleControl ? (
                  <RoleControl member={member} busy={busy} onChangeRole={onChangeRole} />
                ) : (
                  <span className="shrink-0 text-sm text-ink-muted">
                    {boardRoleLabel(t, member.role)}
                  </span>
                )}

                {showRemove ? (
                  <IconButton
                    label={t('boardMembers.remove', { name })}
                    size="sm"
                    busy={busy}
                    disabled={busy}
                    onClick={() => {
                      onRemoveMember(member.id);
                    }}
                  >
                    <X aria-hidden="true" className="size-3.5" />
                  </IconButton>
                ) : null}
              </div>

              {rowError !== undefined ? (
                /*
                 * Opaque already — the row itself sits on
                 * `--color-surface-raised` — so no second sheet is needed under
                 * the text, unlike `InlineEdit`'s `ErrorLine`, whose caller
                 * cannot guarantee that.
                 *
                 * ---------------------------------------------------------------
                 * ⚠ DELIBERATELY **NOT** `role="alert"` — ONE EVENT, ONE CHANNEL
                 * ---------------------------------------------------------------
                 * This node was `role="alert"` until the D4 integration pass,
                 * and the refusal it renders reached a screen reader **twice**:
                 * `lib/boards.ts`'s mutation hooks already `announce(
                 * error.message)` on every failure, and that file's own docstring
                 * forbids a composing component rendering the same event through
                 * a second live region (`D3-BRIEF.md` §5.7).
                 *
                 * The channel is the hook's, not this node's, and the reason is
                 * measured rather than stylistic: the four D4 refusals do **not**
                 * render uniformly live nodes — the add-member error goes through
                 * `TextField`'s `error`, which is `aria-invalid` and nothing
                 * else. So `announce()` is the only channel that covers all four,
                 * and every rendered slot has to be inert or the covered ones
                 * double up.
                 *
                 * What this node is for is `FR-PERM-032`'s *"explained where it
                 * happens"* — the **visible** half, beside the control that was
                 * pressed. That criterion is about placement, not about liveness.
                 */
                <span className="text-xs text-bad">{rowError}</span>
              ) : null}
            </li>
          );
        })}
      </ul>

      {canManageMembers ? (
        <AddMemberForm onAddMember={onAddMember} busy={addBusy} error={addError} />
      ) : null}
    </div>
  );
}

function RoleControl({
  member,
  busy,
  onChangeRole,
}: {
  member: BoardMember;
  busy: boolean;
  onChangeRole: (userId: string, role: BoardRole) => void;
}) {
  const { t } = useInterfaceLanguage();
  /*
   * ⚠ USER DATA, ALREADY RESOLVED, AND NOT ROUTED. `profileName` is
   * deliberately translator-free across ~45 callers (`Avatar.tsx:155–172`)
   * and feeds search haystacks; what is routed is the sentence around the
   * name, never the name.
   */
  const name = profileName(member);
  const roleLabel = boardRoleLabel(t, member.role);

  return (
    <Menu
      align="end"
      trigger={
        // ⚠ `aria-label` IS A WHOLE SLOTTED SENTENCE. It was
        // `` `${roleLabel} — change ${name}'s role` `` — two slots, a
        // separator and a possessive, and a join is not translatable
        // (`profile.connectedTools.scopeTrigger`'s settled ruling).
        //
        // ⚠ Never `disabled` on the trigger — `WorkspaceMemberList`'s shipped
        // reasoning, copied verbatim: a focused control that goes `disabled`
        // mid-interaction drops focus to `<body>`. `aria-busy` carries the
        // signal instead, and the two radio items carry the refusal.
        <button
          type="button"
          aria-busy={busy}
          aria-label={t('boardMembers.roleTrigger', { role: roleLabel, name })}
          className="inline-flex h-7 shrink-0 items-center gap-1 rounded-card border border-border-strong bg-surface-raised px-2 text-sm text-ink"
        >
          {roleLabel}
        </button>
      }
      sections={[
        {
          id: 'role',
          selection: 'radio',
          value: member.role,
          onValueChange: (value) => {
            onChangeRole(member.id, value as BoardRole);
          },
          items: ASSIGNABLE_BOARD_ROLES.map((role) => ({
            id: role,
            label: (
              <span className="flex flex-col items-start gap-0.5 py-0.5 text-left">
                <span className="text-card text-ink">{boardRoleLabel(t, role)}</span>
                <span className="text-xs text-ink-muted">
                  {boardRoleDescription(t, role)}
                </span>
              </span>
            ),
            disabled: busy,
            // The actual change is wired through the section's own
            // `onValueChange` above — `WorkspaceMemberList`'s shipped
            // precedent for a no-op per-item `onSelect` here. `() =>
            // undefined`, not `() => {}`: an empty arrow body trips
            // `@typescript-eslint/no-empty-function` outside test files.
            onSelect: () => undefined,
          })),
        },
      ]}
    />
  );
}

function AddMemberForm({
  onAddMember,
  busy,
  error,
}: {
  onAddMember: (handle: { email: string } | { username: string }) => void;
  busy: boolean;
  error: string | undefined;
}) {
  const { t } = useInterfaceLanguage();
  const [value, setValue] = useState('');

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = value.trim();
    if (trimmed === '') return;
    onAddMember(trimmed.includes('@') ? { email: trimmed } : { username: trimmed });
  }

  // `exactOptionalPropertyTypes`: `TextField`'s own `error?: string` accepts
  // no explicit `undefined` — only an OMITTED key satisfies it, so
  // `error={error}` would not typecheck when this component's own prop is
  // `string | undefined`. Same shape as `BoardTitle`'s `inlineEditProps`.
  const textFieldProps = { ...(error !== undefined ? { error } : {}) };

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-2">
      <TextField
        label={t('boardMembers.addLabel')}
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        wrapperClassName="flex-1"
        {...textFieldProps}
      />
      <Button type="submit" variant="primary" busy={busy}>
        {t('boardMembers.add')}
      </Button>
    </form>
  );
}
